import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api, { fileUrl, formatRupiah, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Edit, QrCode, Share2, MessageSquare, Trash2, Eye, Phone, FileText,
  Building2, Bed, Bath, Expand, Home
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = { baru: "Baru", on_market: "On Market", nego: "Nego", closing: "Closing", sold: "SOLD", reserved: "Reserved" };

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const [report, setReport] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/listings/${id}`);
        setL(data);
        try {
          const { data: r } = await api.get(`/listings/${id}/report`);
          setReport(r);
        } catch {}
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [id]);

  if (!l) return <div className="h-40 rounded-xl bg-[#141C2E] animate-pulse" />;

  const publicUrl = `${window.location.origin}/p/listing/${l.id}`;
  const waLink = `https://wa.me/${(l.agent_phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Halo ${l.agent_name}, saya tertarik dengan listing: ${l.title} - ${publicUrl}`)}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link disalin");
    } catch { toast.error("Gagal menyalin"); }
  };

  const onDelete = async () => {
    if (!window.confirm("Hapus listing ini?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Listing dihapus");
      navigate("/app/listings");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canEdit = user?.id === l.agent_id || user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="listing-detail-page">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Kembali
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-[#0D1322] border border-white/5 relative">
            {l.photos?.length ? (
              <img src={fileUrl(l.photos[activePhoto])} alt="" className="w-full h-full object-contain bg-black" />
            ) : (
              <div className="w-full h-full grid place-items-center text-slate-600"><Building2 className="w-16 h-16" /></div>
            )}
            <div className="absolute top-4 left-4">
              <Badge className="bg-black/60 backdrop-blur-md border-orange-500/30 text-orange-300 uppercase text-[10px] tracking-widest">
                {STATUS_LABELS[l.status]}
              </Badge>
            </div>
          </div>
          {l.photos?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {l.photos.map((p, i) => (
                <button key={p} onClick={() => setActivePhoto(i)} data-testid={`thumb-${i}`}
                        className={`shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 transition ${i === activePhoto ? "border-orange-500" : "border-white/5"}`}>
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="rounded-2xl bg-[#141C2E] border border-white/5 p-6 space-y-5">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-orange-400 font-semibold">{l.property_type}</div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">{l.title}</h1>
              <div className="text-slate-400 text-sm mt-1">{l.location}{l.address ? ` · ${l.address}` : ""}</div>
            </div>
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Harga</div>
                <div className="text-3xl font-extrabold text-orange-400 mt-1">{formatRupiah(l.price)}</div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-xs flex-1 min-w-[260px]">
                <Spec icon={<Expand className="w-3.5 h-3.5" />} label="LT" value={l.land_area ? `${l.land_area} m²` : "—"} />
                <Spec icon={<Home className="w-3.5 h-3.5" />} label="LB" value={l.building_area ? `${l.building_area} m²` : "—"} />
                <Spec icon={<Bed className="w-3.5 h-3.5" />} label="KT" value={l.bedrooms ?? "—"} />
                <Spec icon={<Bath className="w-3.5 h-3.5" />} label="KM" value={l.bathrooms ?? "—"} />
              </div>
            </div>

            {l.facilities?.length > 0 && (
              <div>
                <div className="text-sm font-semibold mb-2">Fasilitas</div>
                <div className="flex flex-wrap gap-2">
                  {l.facilities.map((f) => (
                    <span key={f} className="px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-200 text-xs font-medium">{f}</span>
                  ))}
                </div>
              </div>
            )}

            {l.description && (
              <div>
                <div className="text-sm font-semibold mb-2">Deskripsi</div>
                <p className="text-sm text-slate-300 whitespace-pre-line leading-relaxed">{l.description}</p>
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl bg-[#141C2E] border border-white/5 p-6 space-y-3 sticky top-24">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Agent</div>
              <div className="font-semibold text-slate-100 mt-1">{l.agent_name}</div>
              {l.agent_agency && <div className="text-xs text-slate-400">{l.agent_agency}</div>}
              {l.agent_phone && <div className="text-xs text-slate-500 mt-0.5">{l.agent_phone}</div>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-white/10 bg-[#0D1322] hover:bg-white/5" data-testid="qr-code-btn">
                    <QrCode className="w-4 h-4 mr-1" /> QR Code
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141C2E] border-white/10 text-slate-100">
                  <DialogHeader>
                    <DialogTitle>QR Code Listing</DialogTitle>
                    <DialogDescription className="text-slate-400">Scan untuk buka detail &amp; langsung chat WhatsApp.</DialogDescription>
                  </DialogHeader>
                  <div className="bg-white rounded-xl p-6 grid place-items-center">
                    <QRCodeSVG value={publicUrl} size={240} level="M" bgColor="#ffffff" fgColor="#0A0D14" />
                  </div>
                  <div className="text-xs text-slate-500 break-all">{publicUrl}</div>
                </DialogContent>
              </Dialog>

              <Button variant="outline" onClick={onCopy} className="border-white/10 bg-[#0D1322] hover:bg-white/5" data-testid="copy-link-btn">
                <Share2 className="w-4 h-4 mr-1" /> Copy Link
              </Button>

              <a href={waLink} target="_blank" rel="noreferrer" className="col-span-2" data-testid="whatsapp-btn">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                  <MessageSquare className="w-4 h-4 mr-1" /> Share via WhatsApp
                </Button>
              </a>

              {canEdit && (
                <>
                  <Link to={`/app/listings/${id}/edit`} className="col-span-1">
                    <Button variant="outline" className="w-full border-white/10 bg-[#0D1322]" data-testid="edit-btn"><Edit className="w-4 h-4 mr-1" /> Edit</Button>
                  </Link>
                  <Button onClick={onDelete} variant="outline" className="col-span-1 border-red-500/30 text-red-300 bg-red-500/5 hover:bg-red-500/10" data-testid="delete-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Hapus
                  </Button>
                </>
              )}
            </div>
          </div>

          {report && (
            <div className="rounded-2xl bg-[#141C2E] border border-white/5 p-6" data-testid="report-card">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-orange-400" />
                <h3 className="font-semibold">Laporan untuk Owner</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">Periode: {report.period}</p>
              <div className="grid grid-cols-3 gap-3">
                <Stat icon={<Eye className="w-4 h-4" />} label="Views" value={report.metrics.views} />
                <Stat icon={<Phone className="w-4 h-4" />} label="WA Klik" value={report.metrics.whatsapp_clicks} />
                <Stat icon={<FileText className="w-4 h-4" />} label="Survei" value={report.metrics.survey_requests} />
              </div>
              <div className="mt-3 text-xs text-slate-500">Rata-rata {report.metrics.avg_views_per_day} views/hari</div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div className="flex flex-col items-start p-3 rounded-lg bg-[#0D1322] border border-white/5">
      <div className="flex items-center gap-1 text-slate-500 text-[10px] uppercase tracking-widest">{icon}{label}</div>
      <div className="font-semibold text-slate-100 mt-0.5">{value}</div>
    </div>
  );
}
function Stat({ icon, label, value }) {
  return (
    <div className="p-3 rounded-lg bg-[#0D1322] border border-white/5 text-center">
      <div className="text-slate-500 flex justify-center">{icon}</div>
      <div className="text-xl font-extrabold mt-1">{value}</div>
      <div className="text-[10px] uppercase text-slate-500 tracking-widest">{label}</div>
    </div>
  );
}
